import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import StyledSelect from './StyledSelect';
import { API } from '../lib/api';
import { getCorrectIndices, requiredCorrect } from '../utils/quizEval';
import { useAuth } from '../context/AuthContext';

// Enhanced Live Preview Module Editor
const EnhancedModuleEditor = ({ isOpen, onClose, onSuccess, endpoint = 'teamleiter/modules', editingModule = null, existingCategories = null }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    id: '',
    title: '',
    description: '',
    category_id: '',
    content_type: 'text',
    content: '',
    duration_minutes: '',
    target_teams: [],
    target_roles: ['vertriebler'],
    // Video-specific fields
    video_url: '',
    // Document-specific fields  
    document_url: '',
    // Quiz-specific fields
    quiz_questions: [],
    passing_score_percent: 100,
    // Image-specific fields
    images: []
  });
  const [categories, setCategories] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [categoryImages, setCategoryImages] = useState([]);
  const [loadingCategoryImages, setLoadingCategoryImages] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]);
  const [lastSaved, setLastSaved] = useState(null);
  const [draftId, setDraftId] = useState(null); // ID des Auto-Save-Entwurfs (wird aktualisiert statt dupliziert)
  const [deviceView, setDeviceView] = useState('desktop'); // desktop, mobile
  const [previewRole, setPreviewRole] = useState('vertriebler'); // Role for preview simulation
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true);
  
  // PDF Conversion Progress State
  const [pdfConversionProgress, setPdfConversionProgress] = useState(null);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [conversionPolling, setConversionPolling] = useState(null);

  // PDF Conversion Progress Polling
  const startProgressPolling = (moduleId) => {
    console.log('🔄 Starting PDF conversion progress polling for:', moduleId);
    setShowProgressModal(true);
    
    const pollProgress = async () => {
      try {
        const API = import.meta.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${API}/modules/${moduleId}/pdf-conversion-progress`);
        const progress = response.data;
        
        console.log('📊 Progress update:', progress);
        setPdfConversionProgress(progress);
        
        // Check if conversion is complete or failed
        if (progress.status === 'completed') {
          console.log('✅ PDF conversion completed successfully');
          clearInterval(conversionPolling);
          setConversionPolling(null);
          
          // Auto-close progress modal after 2 seconds
          setTimeout(() => {
            setShowProgressModal(false);
            setPdfConversionProgress(null);
          }, 2000);
          
        } else if (progress.status === 'error') {
          console.log('❌ PDF conversion failed:', progress.message);
          clearInterval(conversionPolling);
          setConversionPolling(null);
          
          // Keep modal open to show error - user can close manually
        }
        
      } catch (error) {
        console.error('❌ Error polling progress:', error);
      }
    };
    
    // Poll immediately, then every 2 seconds
    pollProgress();
    const interval = setInterval(pollProgress, 2000);
    setConversionPolling(interval);
  };
  
  // Role-based preview helper functions
  const canRoleSeeModule = (role, targetRoles) => {
    // Admin and Vertriebsleiter can see everything
    if (role === 'admin' || role === 'vertriebsleiter') {
      return true;
    }
    
    // If no target roles set, everyone can see it
    if (!targetRoles || targetRoles.length === 0) {
      return true;
    }
    
    // Direct role match
    if (targetRoles.includes(role)) {
      return true;
    }
    
    // Hierarchy check: Teamleiter can see Vertriebler content
    if (role === 'teamleiter' && targetRoles.includes('vertriebler')) {
      return true;
    }
    
    return false;
  };
  
  const getRoleContextForPreview = (role, targetRoles) => {
    if (!targetRoles || targetRoles.length === 0) {
      return {
        type: 'allgemein',
        label: 'Allgemeiner Inhalt',
        icon: '📚 '
      };
    }
    
    if (role === 'admin' || role === 'vertriebsleiter') {
      return {
        type: 'einsicht',
        label: 'Einsicht (Admin/Vertriebsleiter)',
        icon: '👁️ '
      };
    }
    
    if (targetRoles.includes(role)) {
      return {
        type: 'pflicht',
        label: 'Pflichtmodul für diese Rolle',
        icon: '⚠️ '
      };
    }
    
    if (role === 'teamleiter' && targetRoles.includes('vertriebler')) {
      return {
        type: 'einsicht',
        label: 'Zur Einsicht (Hierarchie)',
        icon: '👁️ '
      };
    }
    
    return {
      type: 'nicht_sichtbar',
      label: 'Nicht sichtbar für diese Rolle',
      icon: '🚫 '
    };
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (conversionPolling) {
        clearInterval(conversionPolling);
      }
    };
  }, [conversionPolling]);
  const autoSave = useCallback(async () => {
    if (!autoSaveEnabled || !formData.title.trim()) return;
    // Beim Bearbeiten eines bestehenden Moduls ist das Modul selbst die Quelle – kein Entwurf nötig
    // (formData.id wäre hier die Modul-ID, nicht eine Draft-ID → Backend würde 404 liefern)
    if (editingModule) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/drafts/save`, { ...formData, id: draftId || '' });
      if (response.data?.id) setDraftId(response.data.id);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Auto-save failed:', error);
    } finally {
      setSaving(false);
    }
  }, [formData, autoSaveEnabled, editingModule, draftId]);

  // Auto-save every 2 minutes
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(autoSave, 2 * 60 * 1000); // 2 minutes
    return () => clearInterval(interval);
  }, [isOpen, autoSave]);

  // Clear validation errors only when the form data actually changes (not when errors are set)
  const prevFormDataRef = useRef(formData);
  useEffect(() => {
    if (!isOpen) return;
    const changed = prevFormDataRef.current !== formData;
    prevFormDataRef.current = formData;
    if (changed && validationErrors.length > 0) {
      setValidationErrors([]);
      setError('');
    }
  }, [formData, isOpen, validationErrors.length]);

  useEffect(() => {
    if (isOpen) {
      fetchData();
      // Reset validation errors when opening editor
      setValidationErrors([]);
      setError('');
    }
  }, [isOpen, existingCategories]);

  // Load editing module data if provided
  useEffect(() => {
    if (editingModule) {
      console.log('🔄 Loading editing module:', editingModule); // Debug log
      
      // Parse quiz questions if they exist and are in string format
      let parsedQuizQuestions = [];
      if (editingModule.quiz_questions) {
        try {
          parsedQuizQuestions = typeof editingModule.quiz_questions === 'string' 
            ? JSON.parse(editingModule.quiz_questions) 
            : editingModule.quiz_questions;
        } catch (error) {
          console.error('Error parsing quiz questions:', error);
        }
      }
      
      // Parse images if they exist and are in string format
      let parsedImages = [];
      if (editingModule.images) {
        try {
          parsedImages = typeof editingModule.images === 'string' 
            ? JSON.parse(editingModule.images) 
            : editingModule.images;
        } catch (error) {
          console.error('Error parsing images:', error);
        }
      }
      
      const newFormData = {
        id: editingModule.id,
        title: editingModule.title || '',
        description: editingModule.description || '',
        category_id: editingModule.category_id || '',
        content_type: editingModule.content_type || 'text',
        content: editingModule.content || '',
        duration_minutes: editingModule.duration_minutes || '',
        target_teams: editingModule.target_teams || [],
        target_users: editingModule.target_users || [],
        requires_main_academy: editingModule.requires_main_academy ?? true,
        video_url: editingModule.video_url || '',
        document_url: editingModule.document_url || '',
        quiz_questions: parsedQuizQuestions,
        passing_score_percent: editingModule.passing_score_percent ?? 100,
        images: parsedImages
      };
      
      console.log('✅ Setting form data:', newFormData); // Debug log
      setFormData(newFormData);
    }
  }, [editingModule]);

  const fetchData = async () => {
    try {
      // Use existing categories if provided, otherwise fetch them
      if (existingCategories && existingCategories.length > 0) {
        console.log('✅ Using existing categories:', existingCategories.length); // Debug log
        setCategories(existingCategories);
      } else {
        console.log('🔄 Fetching categories from API'); // Debug log
        const categoriesRes = await axios.get(`${API}/categories`);
        setCategories(categoriesRes.data);
      }
      
      // Always fetch team members
      const membersRes = await axios.get(`${API}/teamleiter/users`);
      setTeamMembers(membersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  // Load category images for quiz integration
  const loadCategoryImages = async (categoryId) => {
    if (!categoryId) return;
    
    setLoadingCategoryImages(true);
    try {
      const response = await fetch(`${API}/categories/${categoryId}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCategoryImages(data.images || []);
        console.log(`Loaded ${data.total_images} images from category ${categoryId}`);
      } else {
        console.error('Error loading category images:', response.status);
        setCategoryImages([]);
      }
    } catch (error) {
      console.error('Error loading category images:', error);
      setCategoryImages([]);
    } finally {
      setLoadingCategoryImages(false);
    }
  };

  // Load category images when category changes
  useEffect(() => {
    if (formData.category_id && formData.content_type === 'quiz') {
      loadCategoryImages(formData.category_id);
    } else {
      setCategoryImages([]);
    }
  }, [formData.category_id, formData.content_type]);

  // Image management functions
  const handleImageUpload = async (files) => {
    if (!files || files.length === 0) return;

    setUploadingImages(true);
    try {
      const newImages = [];
      
      for (const file of files) {
        // Create a preview URL for the image
        const reader = new FileReader();
        const imageData = await new Promise((resolve) => {
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(file);
        });
        
        const imageObj = {
          id: Date.now() + Math.random(),
          title: file.name.replace(/\.[^/.]+$/, ""), // Remove extension
          description: '',
          url: imageData, // Base64 data URL for preview
          alt: file.name,
          file: file, // Keep reference to original file
          size: file.size,
          type: file.type
        };
        
        newImages.push(imageObj);
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages]
      }));
      
    } catch (error) {
      console.error('Error uploading images:', error);
      setError('Fehler beim Hochladen der Bilder');
    } finally {
      setUploadingImages(false);
    }
  };

  const updateImageDetails = (imageId, field, value) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map(img => 
        img.id === imageId ? { ...img, [field]: value } : img
      )
    }));
  };

  const removeImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  const moveImage = (dragIndex, dropIndex) => {
    const newImages = [...formData.images];
    const draggedImage = newImages[dragIndex];
    newImages.splice(dragIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    
    setFormData(prev => ({
      ...prev,
      images: newImages
    }));
  };

  // File upload handling
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 50 * 1024 * 1024) {
      setError('Datei ist zu groß. Maximum 50MB.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload/file`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadedFile({
        filename: response.data.filename,
        file_path: response.data.file_path,
        file_size: response.data.file_size,
        content_type: response.data.content_type
      });

      setFormData(prev => ({
        ...prev,
        content: prev.content_type === 'video' || prev.content_type === 'document' || prev.content_type === 'pdf'
          ? response.data.file_path 
          : prev.content
      }));

    } catch (error) {
      setError(error.response?.data?.detail || 'Fehler beim Hochladen der Datei');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // SOFORT Progress Bar anzeigen für PDF-Module
    if (formData.content_type === 'pdf') {
      console.log('🎯 PDF-Modul wird erstellt - Progress Bar wird SOFORT angezeigt');
      setShowProgressModal(true);
      setPdfConversionProgress({
        status: 'starting',
        current_slide: 0,
        total_slides: 0,
        percentage: 0,
        message: 'PDF-Konvertierung wird gestartet...'
      });
      
      // Kurze Verzögerung damit User die Progress Bar sieht
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setValidationErrors([]);

    // Comprehensive validation
    const errors = [];
    
    // Basic field validation
    if (!formData.title.trim()) {
      errors.push('Titel ist erforderlich.');
    }
    
    if (!formData.description.trim()) {
      errors.push('Beschreibung ist erforderlich.');
    }
    
    if (!formData.category_id) {
      errors.push('Kategorie ist erforderlich.');
    }
    
    if (!formData.duration_minutes || formData.duration_minutes <= 0) {
      errors.push('Dauer muss angegeben werden und größer als 0 sein.');
    }

    // Content type specific validation
    switch (formData.content_type) {
      case 'text':
        if (!formData.content.trim()) {
          errors.push('Text-Inhalt ist erforderlich.');
        }
        break;
        
      case 'video':
        const hasVideoFile = uploadedFile && (uploadedFile.file_path || uploadedFile.filename);
        const hasVideoUrl = formData.video_url && formData.video_url.trim();
        
        if (!hasVideoFile && !hasVideoUrl) {
          errors.push('Für Video-Module ist entweder ein Video-Link oder eine Video-Datei erforderlich.');
        }
        
        // Validate video URL format if provided
        if (hasVideoUrl) {
          const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
          const youtubePattern = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
          const vimeoPattern = /^(https?:\/\/)?(www\.)?vimeo\.com\/.+$/;
          
          if (!urlPattern.test(formData.video_url) && !youtubePattern.test(formData.video_url) && !vimeoPattern.test(formData.video_url)) {
            errors.push('Video-Link hat ein ungültiges Format. Bitte verwenden Sie einen gültigen YouTube, Vimeo oder anderen Video-Link.');
          }
        }
        break;
        
      case 'document':
        const hasDocumentFile = uploadedFile && (uploadedFile.file_path || uploadedFile.filename);
        const hasDocumentUrl = formData.document_url && formData.document_url.trim();
        
        if (!hasDocumentFile && !hasDocumentUrl) {
          errors.push('Für Dokument-Module ist entweder ein Dokument-Link oder eine Dokument-Datei erforderlich.');
        }
        
        // Validate document URL format if provided
        if (hasDocumentUrl) {
          const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
          if (!urlPattern.test(formData.document_url)) {
            errors.push('Dokument-Link hat ein ungültiges Format. Bitte verwenden Sie einen gültigen Link.');
          }
        }
        
        // Validate file type if file is uploaded
        if (hasDocumentFile && uploadedFile.filename) {
          const allowedExtensions = ['.pdf', '.doc', '.docx', '.txt', '.ppt', '.pptx'];
          const fileExtension = uploadedFile.filename.toLowerCase().substring(uploadedFile.filename.lastIndexOf('.'));
          
          if (!allowedExtensions.includes(fileExtension)) {
            errors.push(`Datei-Format "${fileExtension}" wird nicht unterstützt. Erlaubte Formate: ${allowedExtensions.join(', ')}`);
          }
        }
        break;
        
      case 'quiz':
        if (!formData.quiz_questions || formData.quiz_questions.length === 0) {
          errors.push('Für Quiz-Module ist mindestens eine Frage erforderlich.');
        } else {
          // Validate quiz questions
          formData.quiz_questions.forEach((question, index) => {
            if (!question.question.trim()) {
              errors.push(`Frage ${index + 1}: Frage-Text ist erforderlich.`);
            }
            
            if (!question.options || question.options.length < 2) {
              errors.push(`Frage ${index + 1}: Mindestens 2 Antwortmöglichkeiten sind erforderlich.`);
            } else {
              // Check if all options have text
              question.options.forEach((option, optionIndex) => {
                let optionText = '';
                if (typeof option === 'string') {
                  optionText = option;
                } else if (option && typeof option === 'object' && option.text) {
                  optionText = option.text;
                }
                
                if (!optionText.trim()) {
                  errors.push(`Frage ${index + 1}, Antwort ${optionIndex + 1}: Antwort-Text ist erforderlich.`);
                }
              });
              
              // Check if at least one option is marked as correct (same logic as server & viewer)
              const correctCount = getCorrectIndices(question).length;
              const hasCorrectAnswer = (question.type || 'single') === 'single' ? correctCount === 1 : correctCount >= 1;
              
              if (!hasCorrectAnswer) {
                errors.push(`Frage ${index + 1}: Mindestens eine Antwort muss als richtig markiert werden.`);
              } else if (question.type === 'multiple' && correctCount === question.options.length) {
                errors.push(`Frage ${index + 1}: Bei Multiple-Choice können nicht alle Antworten richtig sein.`);
              }
            }
          });
        }
        break;
        
      case 'pdf':
        const hasPdfFile = uploadedFile && (uploadedFile.file_path || uploadedFile.filename);
        const hasPdfUrl = formData.document_url && formData.document_url.trim();
        
        if (!hasPdfFile && !hasPdfUrl) {
          errors.push('Für PDF-Module ist entweder ein PDF-Link oder eine PDF-Datei erforderlich.');
        }
        
        // Validate PDF URL format if provided
        if (hasPdfUrl) {
          const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
          if (!urlPattern.test(formData.document_url)) {
            errors.push('PDF-Link hat ein ungültiges Format. Bitte verwenden Sie einen gültigen Link.');
          }
        }
        
        // Validate file type if file is uploaded
        if (hasPdfFile && uploadedFile.filename) {
          const fileExtension = uploadedFile.filename.toLowerCase().substring(uploadedFile.filename.lastIndexOf('.'));
          
          if (fileExtension !== '.pdf') {
            errors.push(`Datei-Format "${fileExtension}" wird nicht unterstützt. Nur PDF-Dateien sind erlaubt.`);
          }
        }
        break;
    }

    // If there are validation errors, show them and stop submission
    if (errors.length > 0) {
      setValidationErrors(errors);
      setError('Bitte korrigieren Sie die folgenden Fehler:');
      setLoading(false);
      return;
    }

    try {
      const moduleData = {
        ...formData,
        duration_minutes: formData.duration_minutes ? parseInt(formData.duration_minutes) : null,
      };

      // Ensure the ID is set for editing mode
      if (editingModule && !moduleData.id) {
        moduleData.id = editingModule.id;
      }

      // Handle file uploads
      if (uploadedFile && (formData.content_type === 'video' || formData.content_type === 'document' || formData.content_type === 'pdf')) {
        if (formData.content_type === 'pdf') {
          // For PDF modules, store the file path in document_url for consistency with API expectations
          moduleData.document_url = uploadedFile.file_path;
          moduleData.content = uploadedFile.file_path; // Also set content for preview
          moduleData.media_file = uploadedFile.file_path;
        } else {
          moduleData.content = uploadedFile.file_path;
          moduleData.media_file = uploadedFile.file_path;
        }
        moduleData.media_file_name = uploadedFile.filename;
        moduleData.media_file_size = uploadedFile.file_size;
      }

      // Handle image uploads - convert images to JSON string with size validation
      if (formData.images && formData.images.length > 0) {
        // Validate total image size to prevent performance issues
        const imagesForSubmission = formData.images.map((img, index) => ({
          id: index,
          title: img.title || `Bild ${index + 1}`,
          description: img.description || '',
          url: img.url, // Base64 data URL
          alt: img.alt || img.title || `Bild ${index + 1}`,
          order: index
        }));
        
        const imagesJson = JSON.stringify(imagesForSubmission);
        
        // Check if total image data exceeds 5MB to prevent performance issues
        if (imagesJson.length > 5 * 1024 * 1024) {
          errors.push('Bilder sind zu groß. Die Gesamtgröße aller Bilder darf 5MB nicht überschreiten.');
          setValidationErrors(errors);
          setError('Bitte reduzieren Sie die Bildgröße oder -anzahl.');
          setLoading(false);
          return;
        }
        
        moduleData.images = imagesJson;
      } else {
        moduleData.images = JSON.stringify([]);
      }

      // For video content, set content based on video_url or uploaded file
      if (formData.content_type === 'video') {
        if (formData.video_url && !uploadedFile) {
          moduleData.content = formData.video_url;
        }
      }

      // For document content, set content based on document_url or uploaded file
      if (formData.content_type === 'document') {
        if (formData.document_url && !uploadedFile) {
          moduleData.content = formData.document_url;
        }
      }

      // For quiz content, serialize quiz questions and set as content
      if (formData.content_type === 'quiz' && formData.quiz_questions && formData.quiz_questions.length > 0) {
        moduleData.content = JSON.stringify({
          questions: formData.quiz_questions
        });
        // Also keep quiz_questions for backend processing
        moduleData.quiz_questions = formData.quiz_questions;
        moduleData.passing_score_percent = formData.passing_score_percent ?? 100;
      }

      console.log('Sending module data:', moduleData);
      console.log('Edit mode:', !!editingModule, 'Module ID:', editingModule?.id); // Debug log

      let response;
      if (editingModule) {
        // Ultra-fast update: Calculate only changed fields for text modules
        if (formData.content_type === 'text' && !uploadedFile && !formData.images?.length) {
          // Fast path for simple text changes
          const changedFields = {};
          
          if (formData.title !== editingModule.title) changedFields.title = formData.title;
          if (formData.description !== editingModule.description) changedFields.description = formData.description;
          if (formData.content !== editingModule.content) changedFields.content = formData.content;
          if (formData.category_id !== editingModule.category_id) changedFields.category_id = formData.category_id;
          if (parseInt(formData.duration_minutes) !== editingModule.duration_minutes) {
            changedFields.duration_minutes = parseInt(formData.duration_minutes) || null;
          }
          
          console.log('🚀 Fast PATCH update with fields:', Object.keys(changedFields));
          
          if (Object.keys(changedFields).length > 0) {
            response = await axios.patch(`${API}/modules/${editingModule.id}`, changedFields);
          } else {
            // No changes, just close
            onSuccess();
            onClose();
            return;
          }
        } else {
          // Full update for complex modules
          console.log(`🔄 Full PUT update for module ${editingModule.id}`);
          response = await axios.put(`${API}/modules/${editingModule.id}`, moduleData);
        }
        console.log('✅ Module updated successfully:', response.data);
      } else {
        // Create new module
        console.log('Creating new module with data:', moduleData); // Debug log
        
        // NO MORE PDF CONVERSION - Direct PDF viewing
        console.log('Creating module (PDF will be displayed directly, no conversion)');
        
        response = await axios.post(`${API}/${endpoint}`, moduleData);
        console.log('Module created successfully:', response.data);
      }
      
      if (response.status === 200 || response.status === 201) {
        console.log('✅ Module created/updated successfully');
        
        // PDF modules are now ready immediately (no conversion needed)
        if (formData.content_type === 'pdf') {
          console.log('📄 PDF module ready for direct viewing (no conversion)');
        }
        
        // Close modal for ALL modules (PDF included - no more waiting for conversion)
        onSuccess();
        if (!editingModule) {
          // Auto-Save-Entwurf ist nach erfolgreichem Anlegen überflüssig
          if (draftId) {
            axios.delete(`${API}/drafts/${draftId}`).catch(err => console.error('Draft cleanup failed:', err));
          }
          // Only reset form when creating new module, not when editing
          resetForm();
        }
        onClose();
      }
    } catch (error) {
      console.error('Error saving module:', error);
      console.error('Error response:', error.response?.data);
      
      // Handle error message properly to avoid rendering objects
      let errorMessage = `Fehler beim ${editingModule ? 'Bearbeiten' : 'Erstellen'} des Moduls`;
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      title: '',
      description: '',
      category_id: '',
      content_type: 'text',
      content: '',
      duration_minutes: '',
      target_teams: [],
      target_roles: ['vertriebler'],
      // Enhanced content type fields
      video_url: '',
      document_url: '',
      quiz_questions: [],
      passing_score_percent: 100
    });
    setUploadedFile(null);
    setDraftId(null);
    setError('');
    setValidationErrors([]);
    setValidationWarnings([]);
    setLastSaved(null);
  };

  // Live Preview Components
  const TextPreview = ({ content, deviceView }) => (
    <div className={`prose max-w-none ${deviceView === 'mobile' ? 'text-sm' : 'text-base'}`}>
      <ReactMarkdown
        components={{
          code({node, inline, className, children, ...props}) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={tomorrow}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          }
        }}
      >
        {content || '*Hier wird der Inhalt angezeigt...*'}
      </ReactMarkdown>
    </div>
  );

  const VideoPreview = ({ content, filename, deviceView }) => {
    if (!content) {
      return (
        <div className="bg-gray-50 rounded-xl p-10 text-center text-gray-400 border-2 border-dashed border-gray-200">
          <div className="text-5xl mb-3">🎥</div>
          <p className="text-sm font-medium">Noch kein Video hinzugefügt</p>
          <p className="text-xs mt-1">Lade eine Datei hoch oder gib einen YouTube / Vimeo Link ein</p>
        </div>
      );
    }

    // Generate embed URL for YouTube / Vimeo
    const getEmbedUrl = (url) => {
      const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
      if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?rel=0`;
      const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
      if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
      return null;
    };

    const isLocalFile = content.startsWith('/uploads/');
    const embedUrl = !isLocalFile ? getEmbedUrl(content) : null;
    const localSrc = isLocalFile ? `${API}/files/${content.split('/').pop()}` : null;
    const playerHeight = deviceView === 'mobile' ? '220px' : '360px';

    return (
      <div className={`${deviceView === 'mobile' ? 'max-w-sm' : 'max-w-full'} mx-auto space-y-3`}>
        <div className="bg-black rounded-xl overflow-hidden shadow-lg">
          {/* Native player for local uploads */}
          {localSrc && (
            <video controls className="w-full" style={{ maxHeight: playerHeight }} preload="metadata">
              <source src={localSrc} type="video/mp4" />
              <source src={localSrc} type="video/webm" />
              Dein Browser unterstützt kein HTML5-Video.
            </video>
          )}
          {/* Embedded player for YouTube / Vimeo */}
          {embedUrl && (
            <iframe
              src={embedUrl}
              className="w-full border-0"
              style={{ height: playerHeight }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title="Video-Vorschau"
            />
          )}
          {/* Fallback for other HTTP URLs */}
          {!localSrc && !embedUrl && (
            <video controls className="w-full" style={{ maxHeight: playerHeight }} preload="metadata">
              <source src={content} />
              Dein Browser unterstützt kein HTML5-Video.
            </video>
          )}
        </div>
        <p className="text-xs text-gray-400 text-center">
          So sehen Vertriebler das Video · Unterstützt: MP4, WebM, YouTube, Vimeo
        </p>
      </div>
    );
  };

  const QuizPreview = ({ quizQuestions = [], deviceView }) => {
    const questions = quizQuestions || [];
    
    return (
      <div className={`space-y-4 ${deviceView === 'mobile' ? 'text-sm' : 'text-base'}`}>
        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Quiz-Vorschau</h3>
          <div className="text-blue-700">
            {questions.length} Frage{questions.length !== 1 ? 'n' : ''}
          </div>
        </div>
        
        {questions.map((question, index) => (
          <div key={index} className="bg-white border rounded-lg p-4">
            <div className="font-medium mb-3">
              {index + 1}. {question.question || 'Neue Frage'}
            </div>
            <div className="space-y-2">
              {question.options?.map((option, optionIndex) => (
                <label key={optionIndex} className="flex items-center space-x-2">
                  <input
                    type={question.type === 'multiple' ? 'checkbox' : 'radio'}
                    name={`question-${index}`}
                    className="text-blue-600"
                    disabled
                    defaultChecked={
                      question.type === 'multiple' 
                        ? question.correct?.includes(optionIndex)
                        : question.correct === optionIndex
                    }
                  />
                  <span className={
                    (question.type === 'multiple' && question.correct?.includes(optionIndex)) ||
                    (question.type !== 'multiple' && question.correct === optionIndex)
                      ? 'text-green-600 font-medium' 
                      : ''
                  }>
                    {option || `Option ${optionIndex + 1}`}
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
        
        {questions.length === 0 && (
          <div className="bg-gray-100 rounded-lg p-8 text-center text-gray-500">
            <div className="text-4xl mb-2">❓</div>
            <div>Quiz-Fragen hier eingeben</div>
          </div>
        )}
      </div>
    );
  };

  const DocumentPreview = ({ content, filename, deviceView }) => {
    const hasContent = content && (content.startsWith('/uploads/') || content.startsWith('http'));
    const docUrl = hasContent
      ? (content.startsWith('http') ? content : `${API}/files/${content.split('/').pop()}`)
      : null;

    if (!hasContent) {
      return (
        <div className="bg-gray-50 rounded-xl p-10 text-center text-gray-400 border-2 border-dashed border-gray-200">
          <div className="text-5xl mb-3">📄</div>
          <p className="text-sm font-medium">Noch kein Dokument hochgeladen</p>
        </div>
      );
    }

    return (
      <div className={`${deviceView === 'mobile' ? 'max-w-sm' : 'max-w-full'} mx-auto space-y-3`}>
        <div className="bg-white rounded-xl overflow-hidden shadow border border-gray-200">
          <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 border-b">
            <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
            </svg>
            <span className="text-xs text-gray-600 font-medium truncate">{filename || 'Dokument'}</span>
          </div>
          <iframe
            src={docUrl}
            className="w-full border-0"
            style={{ height: deviceView === 'mobile' ? '320px' : '460px' }}
            title="Dokument-Vorschau"
          />
        </div>
        <p className="text-xs text-gray-400 text-center">So sehen Vertriebler das Dokument</p>
      </div>
    );
  };

  const PDFPreview = ({ content, filename, deviceView }) => {
    const hasContent = content && (content.startsWith('/uploads/') || content.startsWith('http'));
    const pdfUrl = hasContent
      ? (content.startsWith('http') ? content : `${API}/files/${content.split('/').pop()}`)
      : null;

    if (!hasContent && !formData.document_url) {
      return (
        <div className="bg-gray-50 rounded-xl p-10 text-center text-gray-400 border-2 border-dashed border-gray-200">
          <div className="text-5xl mb-3">📑</div>
          <p className="text-sm font-medium">Noch keine PDF-Datei hochgeladen</p>
          <p className="text-xs mt-1">Lade links eine PDF-Datei hoch um die Vorschau zu sehen</p>
        </div>
      );
    }

    const previewUrl = pdfUrl || formData.document_url;

    return (
      <div className={`${deviceView === 'mobile' ? 'max-w-sm' : 'max-w-full'} mx-auto space-y-3`}>
        {/* Slide viewer simulation */}
        <div className="bg-gray-900 rounded-xl overflow-hidden shadow-lg">
          {/* Slide header bar */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-800">
            <span className="text-xs text-gray-400 font-medium">{filename || 'Modul-PDF'}</span>
            <span className="text-xs text-gray-400">Folie 1 von ?</span>
          </div>
          {/* PDF iframe */}
          <div className="relative" style={{ height: deviceView === 'mobile' ? '320px' : '480px' }}>
            <iframe
              src={`${previewUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              className="w-full h-full border-0"
              title="PDF-Vorschau"
            />
          </div>
          {/* Slide navigation mock */}
          <div className="flex items-center justify-between px-4 py-3 bg-gray-800">
            <button disabled className="px-4 py-1.5 bg-gray-700 text-gray-500 rounded-lg text-xs font-medium cursor-not-allowed">
              ← Zurück
            </button>
            <div className="flex gap-1">
              {[1,2,3].map(i => (
                <span key={i} className={`w-2 h-2 rounded-full ${i === 1 ? 'bg-blue-400' : 'bg-gray-600'}`}/>
              ))}
            </div>
            <button className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium">
              Weiter →
            </button>
          </div>
        </div>
        {/* Note */}
        <p className="text-xs text-gray-400 text-center">
          So sehen Vertriebler die Slides – nach dem Speichern wird jede PDF-Seite zu einem Slide konvertiert
        </p>
      </div>
    );
  };

  const renderPreview = () => {
    switch (formData.content_type) {
      case 'text':
        return <TextPreview content={formData.content} deviceView={deviceView} />;
      case 'video':
        return <VideoPreview content={formData.video_url || uploadedFile?.file_path} filename={uploadedFile?.filename} deviceView={deviceView} />;
      case 'quiz':
        // For quiz, pass the quiz_questions array directly instead of content
        return <QuizPreview quizQuestions={formData.quiz_questions || []} deviceView={deviceView} />;
      case 'document':
        return <DocumentPreview content={formData.document_url || uploadedFile?.file_path} filename={uploadedFile?.filename} deviceView={deviceView} />;
      case 'pdf':
        return <PDFPreview content={formData.document_url || uploadedFile?.file_path} filename={uploadedFile?.filename} deviceView={deviceView} />;
      default:
        return <div className="text-gray-500 text-center">Vorschau wird geladen...</div>;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Modul-Editor mit Live-Vorschau
            </h2>
            {saving && (
              <div className="flex items-center space-x-2 text-blue-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm">Wird gespeichert...</span>
              </div>
            )}
            {lastSaved && (
              <div className="text-sm text-gray-500">
                Zuletzt gespeichert: {lastSaved.toLocaleTimeString()}
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setAutoSaveEnabled(!autoSaveEnabled)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                autoSaveEnabled 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              Auto-Save {autoSaveEnabled ? 'An' : 'Aus'}
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Validation Errors */}
        {(error || validationErrors.length > 0 || validationWarnings.length > 0) && (
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex-shrink-0">
            {error && (
              <div className="mb-2">
                <div className="text-sm font-medium text-red-700 mb-1">Fehler:</div>
                <div className="text-sm text-red-600">
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </div>
              </div>
            )}
            {validationErrors.length > 0 && (
              <div className="mb-2">
                <div className="text-sm font-medium text-red-700 mb-1">Validierungsfehler:</div>
                <div className="space-y-1">
                  {validationErrors.map((error, index) => (
                    <div key={index} className="text-sm text-red-600">
                      • {typeof error === 'string' ? error : error.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {validationWarnings.length > 0 && (
              <div>
                <div className="text-sm font-medium text-yellow-700 mb-1">Warnungen:</div>
                <div className="space-y-1">
                  {validationWarnings.map((warning, index) => (
                    <div key={index} className="text-sm text-yellow-600">
                      • {typeof warning === 'string' ? warning : warning.message}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Main Content - Split View */}
        <div className="flex flex-1 min-h-0">
          {/* Left Side - Form */}
          <div className="w-1/2 p-6 border-r border-gray-200 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titel <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Modulname eingeben..."
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beschreibung <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Kurze Beschreibung des Moduls..."
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Kategorie <span className="text-red-500">*</span>
                    </label>
                    <StyledSelect
                      value={formData.category_id}
                      onChange={(e) => {
                        const selCat = categories.find(c => c.id === e.target.value);
                        setFormData({ ...formData, category_id: e.target.value, target_roles: selCat?.target_roles || ['vertriebler'] });
                      }}
                      required
                    >
                      <option value="">Kategorie wählen</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.icon} {category.title}
                        </option>
                      ))}
                    </StyledSelect>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Typ <span className="text-red-500">*</span>
                    </label>
                    <StyledSelect
                      value={formData.content_type}
                      onChange={(e) => setFormData({ ...formData, content_type: e.target.value })}
                      required
                    >
                      <option value="text">📝 Text</option>
                      <option value="video">🎥 Video</option>
                      <option value="quiz">❓ Quiz</option>
                      <option value="document">📄 Dokument</option>
                      <option value="pdf">📑 PDF-Slides</option>
                    </StyledSelect>
                  </div>
                </div>

                {/* Role-based Targeting - Auto-inherited from category, shown as info */}
                {formData.category_id && categories.find(c => c.id === formData.category_id)?.target_roles?.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                    {categories.find(c => c.id === formData.category_id)?.target_roles?.includes('teamleiter') ? (
                      <>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-200">👥 Teamleiter</span>
                        <span className="text-sm text-blue-700">Zielgruppe wird automatisch von der Kategorie übernommen</span>
                      </>
                    ) : (
                      <>
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 border border-blue-200">💼 Vertriebler</span>
                        <span className="text-sm text-blue-700">Zielgruppe wird automatisch von der Kategorie übernommen</span>
                      </>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Dauer (Minuten) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.duration_minutes}
                    onChange={(e) => setFormData({ ...formData, duration_minutes: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="1"
                    placeholder="Geschätzte Dauer..."
                  />
                </div>
              </div>

              {/* Content Section */}
              <div className="space-y-4">
                <div className="border-t pt-4">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Inhalt</h3>
                  
                  {/* TEXT CONTENT */}
                  {formData.content_type === 'text' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Inhalt (Markdown unterstützt) <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        rows={12}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        placeholder="# Überschrift

**Fett** und *kursiv* Text...

## Abschnitt 2
- Liste
- Punkt 2

[Link](https://example.com)"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Markdown-Formatierung wird unterstützt. Preview rechts verfügbar.
                      </p>
                    </div>
                  )}

                  {/* VIDEO CONTENT */}
                  {formData.content_type === 'video' && (
                    <div className="space-y-4">
                      {/* Video Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Video hochladen
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                          <div className="text-center">
                            <input
                              type="file"
                              onChange={handleFileUpload}
                              accept="video/*"
                              className="hidden"
                              id="video-upload"
                              disabled={uploading}
                            />
                            <label
                              htmlFor="video-upload"
                              className="cursor-pointer text-blue-600 hover:text-blue-500 font-medium"
                            >
                              {uploading ? 'Wird hochgeladen...' : '🎥 Video-Datei auswählen'}
                            </label>
                            
                            {uploadedFile && (
                              <div className="mt-2 text-sm text-green-600">
                                ✓ {uploadedFile.filename}
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-500 mt-2">
                              Unterstützte Formate: MP4, WebM, MOV (max. 50 MB)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* ODER-Trenner */}
                      <div className="flex items-center">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm">ODER</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                      </div>

                      {/* Video-Link */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Video-Link (YouTube, Vimeo, etc.)
                        </label>
                        <input
                          type="url"
                          value={formData.video_url || ''}
                          onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://www.youtube.com/watch?v=..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          YouTube, Vimeo oder direkter Video-Link
                        </p>
                      </div>
                    </div>
                  )}

                  {/* DOCUMENT CONTENT */}
                  {formData.content_type === 'document' && (
                    <div className="space-y-4">
                      {/* Document Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Dokument hochladen
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                          <div className="text-center">
                            <input
                              type="file"
                              onChange={handleFileUpload}
                              accept=".pdf,.doc,.docx,.txt,.ppt,.pptx"
                              className="hidden"
                              id="document-upload"
                              disabled={uploading}
                            />
                            <label
                              htmlFor="document-upload"
                              className="cursor-pointer text-blue-600 hover:text-blue-500 font-medium"
                            >
                              {uploading ? 'Wird hochgeladen...' : '📄 Dokument auswählen'}
                            </label>
                            
                            {uploadedFile && (
                              <div className="mt-2 text-sm text-green-600">
                                ✓ {uploadedFile.filename}
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-500 mt-2">
                              PDF, DOC, DOCX, TXT, PPT, PPTX (max. 50MB)
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* ODER-Trenner */}
                      <div className="flex items-center">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm">ODER</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                      </div>

                      {/* Document Link */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Dokument-Link
                        </label>
                        <input
                          type="url"
                          value={formData.document_url || ''}
                          onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com/document.pdf"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Direkter Link zu einem online verfügbaren Dokument
                        </p>
                      </div>
                    </div>
                  )}

                  {/* PDF CONTENT */}
                  {formData.content_type === 'pdf' && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">📑 PDF-Slides System</h4>
                        <p className="text-xs text-blue-700">
                          PDF wird automatisch in interaktive Slides konvertiert (1 Seite = 1 Slide). 
                          Benutzer müssen alle Slides ansehen, um das Modul abzuschließen.
                        </p>
                      </div>

                      {/* PDF Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          PDF-Datei hochladen
                        </label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                          <div className="text-center">
                            <input
                              type="file"
                              onChange={handleFileUpload}
                              accept=".pdf,application/pdf"
                              className="hidden"
                              id="pdf-upload"
                              disabled={uploading}
                            />
                            <label
                              htmlFor="pdf-upload"
                              className="cursor-pointer text-blue-600 hover:text-blue-500 font-medium"
                            >
                              {uploading ? 'Wird hochgeladen...' : '📑 PDF-Datei auswählen'}
                            </label>
                            
                            {uploadedFile && (
                              <div className="mt-2 text-sm text-green-600">
                                ✓ {uploadedFile.filename}
                              </div>
                            )}
                            
                            <p className="text-xs text-gray-500 mt-2">
                              Nur PDF-Dateien (max. 50MB) - wird automatisch in Slides konvertiert
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* ODER-Trenner */}
                      <div className="flex items-center">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="flex-shrink mx-4 text-gray-400 text-sm">ODER</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                      </div>

                      {/* PDF Link */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          PDF-Link
                        </label>
                        <input
                          type="url"
                          value={formData.document_url || ''}
                          onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          placeholder="https://example.com/document.pdf"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Direkter Link zu einer online verfügbaren PDF-Datei
                        </p>
                      </div>

                      {/* Conversion Info */}
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <h5 className="text-sm font-medium text-yellow-800 mb-1">📊 Nach dem Speichern:</h5>
                        <ul className="text-xs text-yellow-700 space-y-1">
                          <li>• PDF wird automatisch in Slides konvertiert</li>
                          <li>• Jede PDF-Seite wird zu einem separaten Slide</li>
                          <li>• Sie können wichtige Slides markieren</li>
                          <li>• Benutzer müssen alle Slides ansehen (3s pro Slide)</li>
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* QUIZ CONTENT */}
                  {formData.content_type === 'quiz' && (
                    <div className="space-y-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-blue-900 mb-2">Quiz-Editor</h4>
                        <p className="text-xs text-blue-700">
                          Erstellen Sie Fragen mit Mehrfachauswahl. Nutzer können das Quiz nur einmal absolvieren.
                        </p>
                      </div>

                      {/* Passing Score Setting */}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <label className="block text-sm font-semibold text-amber-900 mb-3">
                          Mindest-Bestehensgrenze
                        </label>
                        <div className="flex items-center gap-4">
                          <input
                            type="range"
                            min="10"
                            max="100"
                            step="5"
                            value={formData.passing_score_percent ?? 100}
                            onChange={(e) => setFormData({ ...formData, passing_score_percent: parseInt(e.target.value) })}
                            className="flex-1 accent-amber-500"
                          />
                          <span className="text-2xl font-bold text-amber-700 w-16 text-right">
                            {formData.passing_score_percent ?? 100}%
                          </span>
                        </div>
                        <p className="text-xs text-amber-700 mt-2">
                          Der Vertriebler muss mindestens <strong>{formData.passing_score_percent ?? 100}%</strong> der Fragen richtig beantworten um das Quiz zu bestehen.
                          {(formData.quiz_questions?.length > 0) && (
                            <> Das sind <strong>{requiredCorrect(formData.quiz_questions.length, formData.passing_score_percent ?? 100)}</strong> von <strong>{formData.quiz_questions.length}</strong> Fragen.</>
                          )}
                        </p>
                      </div>

                      {/* Quiz Questions */}
                      <div>
                        <div className="flex justify-between items-center mb-4">
                          <label className="block text-sm font-medium text-gray-700">
                            Fragen <span className="text-red-500">*</span>
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const newQuestions = [...(formData.quiz_questions || [])];
                              newQuestions.push({
                                id: Date.now(),
                                question: '',
                                type: 'single', // Default to single-choice
                                options: ['', '', '', ''],
                                correct_answer: 0, // For single-choice (legacy support)
                                correct_answers: [], // For multiple-choice
                                explanation: '',
                                image: null, // Support for question images
                                image_based: false, // Flag for image-based questions
                                show_category_images: false // Flag to show available category images
                              });
                              setFormData({ ...formData, quiz_questions: newQuestions });
                            }}
                            className="px-3 py-1 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                          >
                            + Frage hinzufügen
                          </button>
                        </div>

                        {(!formData.quiz_questions || formData.quiz_questions.length === 0) && (
                          <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
                            <p>Noch keine Fragen erstellt.</p>
                            <p className="text-sm">Klicken Sie auf "Frage hinzufügen" um zu beginnen.</p>
                          </div>
                        )}

                        {formData.quiz_questions && formData.quiz_questions.map((question, qIndex) => (
                          <div key={question.id} className="border border-gray-300 rounded-lg p-4 mb-4">
                            <div className="flex justify-between items-start mb-3">
                              <h5 className="text-sm font-medium text-gray-700">Frage {qIndex + 1}</h5>
                              <button
                                type="button"
                                onClick={() => {
                                  const newQuestions = formData.quiz_questions.filter((_, i) => i !== qIndex);
                                  setFormData({ ...formData, quiz_questions: newQuestions });
                                }}
                                className="text-red-600 hover:text-red-800 text-sm"
                              >
                                Löschen
                              </button>
                            </div>
                            
                            {/* Question Text */}
                            <div className="mb-3">
                              <input
                                type="text"
                                value={question.question}
                                onChange={(e) => {
                                  const newQuestions = [...formData.quiz_questions];
                                  newQuestions[qIndex].question = e.target.value;
                                  setFormData({ ...formData, quiz_questions: newQuestions });
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Ihre Frage eingeben..."
                                required
                              />
                            </div>

                            {/* Question Type Selector */}
                            <div className="mb-3">
                              <label className="text-xs font-medium text-gray-600 block mb-2">Frage-Typ:</label>
                              <div className="flex space-x-4">
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`questionType-${question.id}`}
                                    value="single"
                                    checked={question.type === 'single' || !question.type}
                                    onChange={() => {
                                      const newQuestions = [...formData.quiz_questions];
                                      newQuestions[qIndex].type = 'single';
                                      // Reset correct answers for single choice
                                      newQuestions[qIndex].correct_answer = 0;
                                      newQuestions[qIndex].correct_answers = [];
                                      setFormData({ ...formData, quiz_questions: newQuestions });
                                    }}
                                    className="mr-2 text-blue-600"
                                  />
                                  <span className="text-sm">🔘 Single-Choice (eine richtige Antwort)</span>
                                </label>
                                <label className="flex items-center">
                                  <input
                                    type="radio"
                                    name={`questionType-${question.id}`}
                                    value="multiple"
                                    checked={question.type === 'multiple'}
                                    onChange={() => {
                                      const newQuestions = [...formData.quiz_questions];
                                      newQuestions[qIndex].type = 'multiple';
                                      // Initialize correct_answers array for multiple choice
                                      if (!newQuestions[qIndex].correct_answers) {
                                        newQuestions[qIndex].correct_answers = [];
                                      }
                                      setFormData({ ...formData, quiz_questions: newQuestions });
                                    }}
                                    className="mr-2 text-green-600"
                                  />
                                  <span className="text-sm">☑️ Multiple-Choice (mehrere richtige Antworten)</span>
                                </label>
                              </div>
                            </div>

                            {/* Category Images Integration for Quiz */}
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                              <div className="flex items-center justify-between mb-3">
                                <label className="text-xs font-medium text-blue-800">
                                  🖼️ Kategorie-Bilder für Quiz verwenden
                                </label>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newQuestions = [...formData.quiz_questions];
                                    newQuestions[qIndex].show_category_images = !newQuestions[qIndex].show_category_images;
                                    setFormData({ ...formData, quiz_questions: newQuestions });
                                  }}
                                  className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                                    question.show_category_images 
                                      ? 'bg-blue-600 text-white' 
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                >
                                  {question.show_category_images ? 'Ausblenden' : 'Anzeigen'}
                                </button>
                              </div>
                              
                              {question.show_category_images && (
                                <div>
                                  {loadingCategoryImages ? (
                                    <div className="text-center py-4 text-blue-600">
                                      🔄 Lade Kategorie-Bilder...
                                    </div>
                                  ) : categoryImages.length > 0 ? (
                                    <div>
                                      <p className="text-xs text-blue-700 mb-3">
                                        {categoryImages.length} Bilder aus {formData.category_id} verfügbar. 
                                        Klicken Sie auf ein Bild, um es zu dieser Frage hinzuzufügen.
                                      </p>
                                      <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto">
                                        {categoryImages.map((img) => (
                                          <div 
                                            key={img.id}
                                            className={`relative border-2 rounded-lg cursor-pointer transition-all ${
                                              question.image?.id === img.id 
                                                ? 'border-blue-500 bg-blue-100' 
                                                : 'border-gray-200 hover:border-blue-300'
                                            }`}
                                            onClick={() => {
                                              const newQuestions = [...formData.quiz_questions];
                                              if (question.image?.id === img.id) {
                                                // Remove image if already selected
                                                newQuestions[qIndex].image = null;
                                                newQuestions[qIndex].image_based = false;
                                              } else {
                                                // Set new image
                                                newQuestions[qIndex].image = img;
                                                newQuestions[qIndex].image_based = true;
                                              }
                                              setFormData({ ...formData, quiz_questions: newQuestions });
                                            }}
                                          >
                                            <img 
                                              src={img.url} 
                                              alt={img.alt}
                                              className="w-full h-20 object-cover rounded-lg"
                                            />
                                            {question.image?.id === img.id && (
                                              <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                                ✓
                                              </div>
                                            )}
                                            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-1 rounded-b-lg">
                                              <p className="text-xs truncate">{img.title}</p>
                                              <p className="text-xs text-gray-300">aus: {img.module_title}</p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                      
                                      {question.image && (
                                        <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                                          <div className="flex items-center text-green-800 text-sm">
                                            <span className="mr-1">✅</span>
                                            Ausgewähltes Bild: <strong className="ml-1">{question.image.title}</strong>
                                          </div>
                                          <div className="text-green-600 text-xs mt-1">
                                            Aus Modul: {question.image.module_title} ({question.image.module_type.toUpperCase()})
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="text-center py-4 text-gray-500">
                                      <p className="text-sm">📷 Keine Bilder in dieser Kategorie gefunden</p>
                                      <p className="text-xs">Fügen Sie zuerst Bilder zu den Modulen dieser Kategorie hinzu</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Answer Options */}
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-gray-600">Antwortmöglichkeiten:</label>
                              {question.options.map((option, oIndex) => {
                                const questionType = question.type || 'single';
                                const isCorrect = questionType === 'single' 
                                  ? question.correct_answer === oIndex
                                  : (question.correct_answers || []).includes(oIndex);
                                
                                return (
                                  <div key={oIndex} className="flex items-center space-x-2">
                                    <input
                                      type={questionType === 'single' ? 'radio' : 'checkbox'}
                                      name={questionType === 'single' ? `correct-${question.id}` : undefined}
                                      checked={isCorrect}
                                      onChange={() => {
                                        const newQuestions = [...formData.quiz_questions];
                                        
                                        if (questionType === 'single') {
                                          // Single choice: set correct_answer
                                          newQuestions[qIndex].correct_answer = oIndex;
                                        } else {
                                          // Multiple choice: toggle in correct_answers array
                                          if (!newQuestions[qIndex].correct_answers) {
                                            newQuestions[qIndex].correct_answers = [];
                                          }
                                          
                                          const correctAnswers = [...newQuestions[qIndex].correct_answers];
                                          const answerIndex = correctAnswers.indexOf(oIndex);
                                          
                                          if (answerIndex > -1) {
                                            correctAnswers.splice(answerIndex, 1);
                                          } else {
                                            correctAnswers.push(oIndex);
                                          }
                                          
                                          newQuestions[qIndex].correct_answers = correctAnswers.sort();
                                        }
                                        
                                        setFormData({ ...formData, quiz_questions: newQuestions });
                                      }}
                                      className={questionType === 'single' ? 'text-green-600' : 'text-blue-600'}
                                    />
                                    <input
                                      type="text"
                                      value={typeof option === 'object' ? option.text : option}
                                      onChange={(e) => {
                                        const newQuestions = [...formData.quiz_questions];
                                        newQuestions[qIndex].options[oIndex] = e.target.value;
                                        setFormData({ ...formData, quiz_questions: newQuestions });
                                      }}
                                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      placeholder={`Antwort ${oIndex + 1}`}
                                      required
                                    />
                                    <span className="text-xs text-gray-500 min-w-16">
                                      {isCorrect ? '✓ Richtig' : ''}
                                    </span>
                                  </div>
                                );
                              })}
                              <p className="text-xs text-gray-500">
                                {question.type === 'multiple' 
                                  ? `🔢 Wählen Sie alle richtigen Antworten durch Anklicken der Checkboxen. (${(question.correct_answers || []).length} ausgewählt)`
                                  : '🔘 Wählen Sie die richtige Antwort durch Anklicken des Radiobuttons.'
                                }
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* IMAGES SECTION - Available for all content types */}
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="text-3xl mr-3">🖼️</div>
                      <div>
                        <h3 className="text-lg font-semibold text-blue-900">Modul-Bilder</h3>
                        <p className="text-blue-700 text-sm">Fügen Sie wichtige Bilder hinzu, die Vertriebler ansehen müssen</p>
                      </div>
                    </div>

                    {/* Image Upload Area */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Bilder hochladen
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={(e) => handleImageUpload(Array.from(e.target.files))}
                          className="hidden"
                          id="image-upload"
                          disabled={uploadingImages}
                        />
                        <label 
                          htmlFor="image-upload" 
                          className="cursor-pointer block"
                        >
                          <div className="text-4xl mb-2">📸</div>
                          <p className="text-gray-600 font-medium">
                            {uploadingImages ? 'Bilder werden hochgeladen...' : 'Klicken Sie hier oder ziehen Sie Bilder hierher'}
                          </p>
                          <p className="text-gray-500 text-sm mt-1">
                            PNG, JPG, GIF bis 10MB (mehrere Dateien möglich)
                          </p>
                        </label>
                      </div>
                    </div>

                    {/* Uploaded Images List */}
                    {formData.images.length > 0 && (
                      <div className="space-y-4">
                        <h4 className="font-medium text-gray-900">
                          Hochgeladene Bilder ({formData.images.length})
                        </h4>
                        <div className="space-y-3">
                          {formData.images.map((image, index) => (
                            <div key={image.id} className="bg-white border border-gray-200 rounded-lg p-4">
                              <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                  <img
                                    src={image.url}
                                    alt={image.alt || `Bild ${index + 1}`}
                                    className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                  />
                                </div>
                                
                                <div className="flex-1 space-y-3">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Titel
                                    </label>
                                    <input
                                      type="text"
                                      value={image.title || ''}
                                      onChange={(e) => updateImageDetails(image.id, 'title', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      placeholder="Titel des Bildes..."
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Beschreibung
                                    </label>
                                    <textarea
                                      value={image.description || ''}
                                      onChange={(e) => updateImageDetails(image.id, 'description', e.target.value)}
                                      rows="2"
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      placeholder="Beschreibung des Bildinhalts..."
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Alt-Text (für Barrierefreiheit)
                                    </label>
                                    <input
                                      type="text"
                                      value={image.alt || ''}
                                      onChange={(e) => updateImageDetails(image.id, 'alt', e.target.value)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                      placeholder="Erklärung, was auf dem Bild zu sehen ist..."
                                    />
                                  </div>
                                  
                                  <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>
                                      {image.type} • {Math.round(image.size / 1024)}KB
                                    </span>
                                    <div className="flex items-center space-x-2">
                                      <span className="text-blue-600 font-medium">Position {index + 1}</span>
                                      <button
                                        type="button"
                                        onClick={() => removeImage(image.id)}
                                        className="text-red-600 hover:text-red-800 font-medium"
                                      >
                                        Entfernen
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <div className="flex items-center text-yellow-800">
                            <div className="text-lg mr-2">⚠️</div>
                            <div className="text-sm">
                              <strong>Wichtig:</strong> Vertriebler müssen alle Bilder vollständig ansehen, 
                              bevor sie das Modul abschließen können.
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Target Assignment */}
                <div className="border-t pt-4">
                  <div className="space-y-3">
                    {/* PDF Info - no conversion needed */}
                    {formData.content_type === 'pdf' && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="text-2xl mr-3">📄</div>
                          <div>
                            <h4 className="text-sm font-semibold text-blue-900">PDF-Direktanzeige</h4>
                            <p className="text-xs text-blue-700">PDF wird direkt im Browser angezeigt - keine Konvertierung erforderlich</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>


              {/* Form Actions */}

              <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg font-medium transition-colors disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={loading || validationErrors.length > 0}
                  className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                    (loading || validationErrors.length > 0)
                      ? 'bg-gray-400 cursor-not-allowed text-gray-600' 
                      : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {loading 
                    ? (editingModule ? 'Wird gespeichert...' : 'Wird erstellt...') 
                    : (editingModule ? 'Modul speichern' : 'Modul erstellen')
                  }
                </button>
              </div>
            </form>
          </div>

          {/* Right Side - Enhanced Live Preview with Role Simulation */}
          <div className="w-1/2 p-6 bg-gray-50 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 pb-4 mb-4 border-b border-gray-200">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-lg font-medium text-gray-900">Live-Vorschau</h3>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => setDeviceView('desktop')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      deviceView === 'desktop'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    💻 Desktop
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeviceView('mobile')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      deviceView === 'mobile'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📱 Mobile
                  </button>
                </div>
              </div>
              
              {/* Role Preview Selector */}
              <div className="bg-white rounded-lg p-3 border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  👁️ Vorschau als Rolle:
                </label>
                <select
                  value={previewRole || 'vertriebler'}
                  onChange={(e) => setPreviewRole(e.target.value)}
                  className="w-full px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="vertriebler">🎯 Vertriebler</option>
                  <option value="teamleiter">👥 Teamleiter</option>
                  <option value="vertriebsleiter">📊 Vertriebsleiter</option>
                  <option value="admin">⚙️ Admin</option>
                </select>
                
                {/* Role Context Indicator */}
                {formData.target_roles && formData.target_roles.length > 0 && (
                  <div className="mt-2 text-xs">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      getRoleContextForPreview(previewRole || 'vertriebler', formData.target_roles).type === 'pflicht'
                        ? 'bg-red-100 text-red-800'
                        : getRoleContextForPreview(previewRole || 'vertriebler', formData.target_roles).type === 'einsicht'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {getRoleContextForPreview(previewRole || 'vertriebler', formData.target_roles).icon}
                      {getRoleContextForPreview(previewRole || 'vertriebler', formData.target_roles).label}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Enhanced Preview Content */}
            <div className={`mx-auto transition-all duration-300 ${
              deviceView === 'mobile' ? 'max-w-sm border-2 border-gray-300 rounded-lg bg-white p-4' : 'max-w-4xl'
            }`}>
              
              {/* Role-specific visibility warning */}
              {!canRoleSeeModule(previewRole || 'vertriebler', formData.target_roles) && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2 text-red-800">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-sm font-medium">
                      Nicht sichtbar für {previewRole}
                    </span>
                  </div>
                  <p className="text-xs text-red-700 mt-1">
                    Dieses Modul ist nicht für die ausgewählte Rolle konfiguriert und würde in der Anwendung nicht angezeigt werden.
                  </p>
                </div>
              )}
              
              {/* Module Header */}
              <div className="mb-6">
                <h1 className={`font-bold text-gray-900 mb-2 ${
                  deviceView === 'mobile' ? 'text-lg' : 'text-2xl'
                }`}>
                  {formData.title || 'Modulname'}
                </h1>
                <p className={`text-gray-600 mb-4 ${
                  deviceView === 'mobile' ? 'text-sm' : 'text-base'
                }`}>
                  {formData.description || 'Beschreibung des Moduls'}
                </p>
                <div className="flex items-center flex-wrap gap-2 text-sm">
                  <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                    🕐 {formData.duration_minutes || '?'} Min
                  </span>
                  <span className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-800 rounded-full">
                    📂 {categories.find(c => c.id === formData.category_id)?.title || 'Kategorie'}
                  </span>
                  {formData.target_roles && formData.target_roles.length > 0 && (
                    <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full">
                      🎯 {formData.target_roles.join(', ')}
                    </span>
                  )}
                </div>
              </div>

              {/* Content Preview */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                {renderPreview()}
              </div>
              
              {/* Role-specific Footer Info */}
              <div className="mt-4 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
                <div className="flex items-center space-x-1 mb-1">
                  <span>👁️</span>
                  <span className="font-medium">Vorschau für: {previewRole}</span>
                </div>
                <div>
                  So würde dieses Modul für einen {previewRole} in der Anwendung angezeigt werden.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedModuleEditor;
